import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Message, MessageService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { ConfigOptions } from 'src/app/demo/models/common-data-types';
import {
    disableFormControl,
    enableFormControl,
} from 'src/app/demo/models/form-utils';
import { CreateVehicleRequest } from 'src/app/demo/models/request-data-types';
import { LanguageService } from 'src/app/demo/service/language.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { SelfcareConfigService } from 'src/app/demo/service/selfcare-config.service';
import { VehiclesService } from 'src/app/demo/service/vehicles.service';
import { Option } from 'src/app/demo/models/common-data-types';
import {
    showErrorToast,
    showSuccessToast,
} from 'src/app/demo/utilities/toast-utils';
import { mapToVehicle } from 'src/app/demo/utilities/vehicles.util';
import { InputNumberModule } from 'primeng/inputnumber';
import { CustomToastrService } from 'src/app/demo/service/custom-toastr.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-register-vehicle',
    standalone: true,
    imports: [
        ToastModule,
        CardModule,
        CommonModule,
        ReactiveFormsModule,
        InputTextModule,
        InputNumberModule,
        ButtonModule,
        MessagesModule,
        DropdownModule,
        FormsModule,
        TranslateModule,
        CalendarModule,
        DividerModule,
        SharedModule,
    ],
    templateUrl: './register-vehicle.component.html',
    styleUrl: './register-vehicle.component.scss',
    providers: [
        MessageService,
        ProgressLoaderService,
        MessagesModule,
        DecimalPipe,
    ],
})
export class RegisterVehicleComponent implements OnInit {
    vehicleForm!: FormGroup;
    vehicleMetadata!: any;
    vehicleModels!: any;
    selectedVehicleUsage: any;
    configOptions!: ConfigOptions;
    messages: Message[] = [];
    selectedLanguage: any = null;

    vehicleTypes: Option[] = [];
    vehicleUsages: Option[] = [];
    weightCategories: Option[] = [];

    selectedYear!: Date;
    yearRange: string = '1900:2030';
    minDate: Date = new Date(1900, 0, 1);
    maxDate: Date = new Date();

    constructor(
        private messageService: MessageService,
        private vehiclesService: VehiclesService,
        private configService: SelfcareConfigService,
        private router: Router,
        private fb: FormBuilder,
        private wiseToastr: CustomToastrService,
        private progressLoaderService: ProgressLoaderService,
        private languageService: LanguageService,
        private translate: TranslateService
    ) {
        this.selectedYear = new Date();
    }

    ngOnInit() {
        this.initializeVehicleForm();
        this.fetchVehicleMetadata();
        this.fetchVehicleConfigProperties();
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
            
        });
    }

    setLanguage(language: string) {
        this.translate.use(language);
        this.languageService.setLanguage(language);
        this.translateDropdownOptions();
    }

    initializeVehicleForm() {
        this.vehicleForm = this.fb.group({
            model: [{ value: '', disabled: true }, Validators.required],
            yearOfManufacture: [null, Validators.required],
            carryingCapacity: ['', Validators.required],
            engineCapacity: ['', Validators.required],
            weight: [{ value: '', disabled: true }, Validators.required],
            vehicleValue: [null, Validators.required],
            vehicleUse: ['', Validators.required],
            registrationNumber: ['', Validators.required],
            vehicleType: ['', Validators.required],
        });
        const vehicleModel: FormControl = this.vehicleForm.get(
            'model'
        ) as FormControl;
        disableFormControl(vehicleModel, '');
        this.onChanges();
    }

    onChanges(): void {
        this.vehicleForm
            .get('make')
            ?.valueChanges.subscribe((selectedVehicleMake) => {
                this.getVehicleModels(selectedVehicleMake);
            });
    }

    fetchVehicleMetadata() {
        this.progressLoaderService.show('Fetching vehicle metadata');
        this.configService.getVehicleMetadata().subscribe({
            next: (res) => {
                this.progressLoaderService.hide();
                this.vehicleMetadata = res;
            },
            error: (err) => {
                this.progressLoaderService.hide();
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
            },
        });
    }

    fetchVehicleConfigProperties() {
        this.configService.getVehicleConfigOptions().subscribe({
            next: (res) => {
                this.configOptions = res;
                this.translateVehicleConfigs(this.configOptions);
                enableFormControl(
                    this.vehicleForm.get('weight') as FormControl,
                    '',
                    Validators.required
                );
            },
            error: (err) => {
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
            },
        });
    }

    translateVehicleConfigs(configOptions: ConfigOptions) {
        this.vehicleTypes = this.configOptions.vehicleTypes.map((e) => {
            let translatedText = '';
            this.translate.get(e.description).subscribe((result) => {
                translatedText = result;
            });
            return {
                name: translatedText,
                value: e.name,
            };
        });

        this.vehicleUsages = this.configOptions.vehicleUseOptions.map((e) => {
            let translatedText = '';
            this.translate.get(e.description).subscribe((result) => {
                translatedText = result;
            });
            return {
                name: translatedText,
                value: e.name,
            };
        });

        this.weightCategories = this.configOptions.vehicleWeightOptions.map(
            (e) => {
                let translatedText = '';
                this.translate.get(e.description).subscribe((result) => {
                    translatedText = result;
                });
                return {
                    name: translatedText,
                    value: e.name,
                };
            }
        );
    }

    translateDropdownOptions() {
        const weightCategoryTranslations$ = forkJoin(
            this.weightCategories.map((e) =>
                this.translate.get(e.name).pipe(
                    map((translatedText) => ({
                        name: translatedText,
                        value: e.value,
                    }))
                )
            )
        );

        const vehicleTypeTranslations$ = forkJoin(
            this.vehicleTypes.map((e) =>
                this.translate.get(e.name).pipe(
                    map((translatedText) => ({
                        name: translatedText,
                        value: e.value,
                    }))
                )
            )
        );

        const vehicleUsageTranslations$ = forkJoin(
            this.vehicleUsages.map((e) =>
                this.translate.get(e.name).pipe(
                    map((translatedText) => ({
                        name: translatedText,
                        value: e.value,
                    }))
                )
            )
        );

        // Wait for all translations to complete
        forkJoin({
            weightCategories: weightCategoryTranslations$,
            vehicleTypes: vehicleTypeTranslations$,
            vehicleUsages: vehicleUsageTranslations$,
        }).subscribe(({ weightCategories, vehicleTypes, vehicleUsages }) => {
            this.weightCategories = weightCategories;
            this.vehicleTypes = vehicleTypes;
            this.vehicleUsages = vehicleUsages;
        });
    }

    goBack() {
        this.router.navigate(['/home/vehicles']);
    }

    clearForm() {
        this.vehicleForm.reset();
    }

    saveVehicle() {
        if (this.vehicleForm.valid) {
            this.progressLoaderService.show('Saving vehicle');
            const vehicleRequest: CreateVehicleRequest = mapToVehicle(
                this.vehicleForm.value
            );
            this.vehiclesService.addVehicle(vehicleRequest).subscribe({
                next: (res) => {
                    this.progressLoaderService.hide();
                    this.showSuccessToast(
                        'Vehicle ' +
                            vehicleRequest.registrationNumber +
                            ' was added successfully. You now need to upload vehicle documents'
                    );
                    setTimeout(
                        () =>
                            this.router.navigate(['/home/vehicles', res['id']]),
                        4000
                    );
                },

                error: (err) => {
                    this.progressLoaderService.hide();
                    this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
                },
            });
        } else {
            this.showWarning('Please fill all required fields');
        }
    }

    onModelChange(event: any) {
        this.getVehicleModels(event.value.id);
    }

    getVehicleModels(makeId: string): void {
        this.progressLoaderService.show('Fetching vehicle models');
        this.configService.getVehicleModels(makeId).subscribe({
            next: (value) => {
                this.vehicleModels = value;
                enableFormControl(
                    this.vehicleForm.get('model') as FormControl,
                    '',
                    Validators.required
                );
                this.progressLoaderService.hide();
            },
            error: (err) => {
                this.progressLoaderService.hide();
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
            },
        });
    }

    goBackClicked() {
        this.router.navigate(['/home/vehicles']);
    }

    showSuccessToast(message: string) {
        this.wiseToastr.showSuccess(message);
    }

    showErrorToast(message: string) {
        this.wiseToastr.showError(message);
    }

    showWarning(message: string) {
        this.wiseToastr.showWarning(message);
    }
}
